import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DOMAINS, csvToList } from "@/lib/semantics";
import { SEMANTICS } from "@/constants/testIds";
import api from "@/lib/api";

const EMPTY = {
  name: "", definition: "", semantic_expression: "", sql_template: "",
  source_tables: "", default_grain: "", dimensions: "", valid_filters: "",
  synonyms: "", domain: "Operations", status: "draft",
};

export function MetricDialog({ open, onOpenChange, workspaceId, metric, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const editing = !!metric;

  useEffect(() => {
    if (!open) return;
    if (metric) {
      setForm({
        name: metric.name || "",
        definition: metric.definition || "",
        semantic_expression: metric.semantic_expression || "",
        sql_template: metric.sql_template || "",
        source_tables: (metric.source_tables || []).join(", "),
        default_grain: (metric.default_grain || []).join(", "),
        dimensions: (metric.dimensions || []).join(", "),
        valid_filters: (metric.valid_filters || []).join(", "),
        synonyms: (metric.synonyms || []).join(", "),
        domain: metric.domain || "Operations",
        status: metric.status || "draft",
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, metric]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    const body = {
      name: form.name.trim(),
      definition: form.definition,
      semantic_expression: form.semantic_expression,
      sql_template: form.sql_template,
      source_tables: csvToList(form.source_tables),
      default_grain: csvToList(form.default_grain),
      dimensions: csvToList(form.dimensions),
      valid_filters: csvToList(form.valid_filters),
      synonyms: csvToList(form.synonyms),
      domain: form.domain,
      status: form.status,
    };
    try {
      if (editing) {
        await api.patch(`/semantics/metrics/${metric.id}`, body);
        toast.success("Metric updated");
      } else {
        await api.post("/semantics/metrics", { workspace_id: workspaceId, ...body });
        toast.success("Metric created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={SEMANTICS.metricDialog} className="max-h-[90vh] overflow-y-auto font-mono sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit metric" : "New metric"}</DialogTitle>
          <DialogDescription className="text-xs">
            Define a governed, reusable metric. Active metrics resolve by name in chat.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Field label="Name">
            <Input data-testid={SEMANTICS.metricName} value={form.name} onChange={set("name")} placeholder="monthly_active_users" className="text-sm" />
          </Field>
          <Field label="Definition">
            <Textarea data-testid={SEMANTICS.metricDefinition} value={form.definition} onChange={set("definition")} placeholder="Distinct residents active in a calendar month." rows={2} className="text-sm" />
          </Field>
          <Field label="SQL template">
            <Textarea data-testid={SEMANTICS.metricSql} value={form.sql_template} onChange={set("sql_template")} placeholder="SELECT count(DISTINCT resident_id) FROM clinical.residents WHERE {{TIME_FILTER}}" rows={3} className="text-xs" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Grain (comma-sep)">
              <Input data-testid={SEMANTICS.metricGrain} value={form.default_grain} onChange={set("default_grain")} placeholder="resident_id, month" className="text-sm" />
            </Field>
            <Field label="Dimensions (comma-sep)">
              <Input data-testid={SEMANTICS.metricDimensions} value={form.dimensions} onChange={set("dimensions")} placeholder="facility_id, state" className="text-sm" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Synonyms (comma-sep)">
              <Input data-testid={SEMANTICS.metricSynonyms} value={form.synonyms} onChange={set("synonyms")} placeholder="MAU, active users" className="text-sm" />
            </Field>
            <Field label="Source tables (comma-sep)">
              <Input value={form.source_tables} onChange={set("source_tables")} placeholder="clinical.residents" className="text-sm" />
            </Field>
          </div>
          <Field label="Valid filters (comma-sep)">
            <Input value={form.valid_filters} onChange={set("valid_filters")} placeholder="facility_id, payer" className="text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Domain">
              <Select value={form.domain} onValueChange={set("domain")}>
                <SelectTrigger data-testid={SEMANTICS.metricDomain} className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{DOMAINS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid={SEMANTICS.metricStatus} className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="deprecated">Deprecated</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="text-xs">Cancel</Button>
          <Button data-testid={SEMANTICS.metricSubmit} disabled={busy} onClick={submit} className="bg-accent text-xs uppercase tracking-[0.14em] text-accent-foreground hover:bg-accent/90">
            {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            {editing ? "Save" : "Create metric"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
